import React, { useState, useEffect, useContext, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileUpload, faCheckCircle, faUserShield, faSave, faPaperPlane, faExclamationTriangle, faFileInvoice, faFilePdf, faRotateLeft, faColumns } from '@fortawesome/free-solid-svg-icons';
import Layout from '../../components/Layout';

import Footer from '../../components/ui/Footer';
import OutgoingInvoiceScreen1 from './OutgoingInvoiceScreen1';
import OutgoingInvoiceScreen2 from './OutgoingInvoiceScreen2';
import OutgoingInvoiceScreen3 from './OutgoingInvoiceScreen3';
import invoicingService from '../../services/invoicingService';
import { useFlashMessage } from '../../components/FlashMessage';
import { AuthContext } from '../../context/AuthContext';
import FinancialApprovalModal from '../../components/Invoicing/FinancialApprovalModal';
import InvoiceValidationModal from '../../components/Invoicing/InvoiceValidationModal';
import InvoiceAdjustmentModal from '../../components/Invoicing/InvoiceAdjustmentModal';
import PdfPreviewModal from '../../components/shared/PdfPreviewModal';
import DeliveryLinkBadge from '../../components/Invoicing/DeliveryLinkBadge';
import AbacusStatus from '../../components/Abacus/AbacusStatus';
import HMSPageHeader from '../../components/ui/HMSPageHeader';
import HMSStepper from '../../components/ui/HMSStepper';
import DocumentsSplitPane from '../../components/DocumentsSplitPane';
import RelatedDocumentsPane, { useInvoicePapersOpen } from '../../components/RelatedDocumentsPane';
import useDocumentReturn from '../../components/shared/useDocumentReturn';
import { useSendToFinance, SendToFinanceNote } from '../../components/Invoicing/useSendToFinance';
import RecordLoadProblem, { describeLoadFailure } from '../../components/ui/RecordLoadProblem';
import './OutgoingInvoice.css';
import { extractApiError } from '../../utils/errorMessages';
import { lineNet } from '../../utils/precision';
import PinToDeskButton from '../../components/mydesk/PinToDeskButton';
import { pledgeOfDocument, pledgePayload } from '../../components/shared/PledgeBankField';

/**
 * Create/Edit Outgoing Invoice - 3-Screen Flow Orchestrator
 * 
 * Manages:
 * - State for all 3 screens
 * - Navigation between screens
 * - Save draft functionality
 * - Submit for workflow
 * - Creation from delivery or pro-forma
 * - Editing existing invoices
 */

const CreateOutgoingInvoice = () => {
  const history = useHistory();
  const { showSuccess, showError } = useFlashMessage();
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const { id } = useParams();
  
  // Check if creating from delivery or pro-forma
  const sourceDelivery = location.state?.delivery || null;
  const sourceProforma = location.state?.proforma || null;
  
  // State management
  const [currentScreen, setCurrentScreen] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(!!id);
  // Row 125: why the invoice in the address didn't load. While set, the screen
  // shows RecordLoadProblem INSTEAD of the editor, and the id that did load is
  // what lets a save through -- a blank editor must never be saved over the
  // real invoice.
  const [loadProblem, setLoadProblem] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadedId, setLoadedId] = useState(null);
  // The id of the document this screen is editing -- from the URL when we
  // arrived on one, or adopted from the server the moment a draft is first
  // created. Held here so a second Save Draft UPDATES the document instead of
  // POSTing a duplicate.
  //
  // isEditMode used to be useState(!!id), and setIsEditMode was never called
  // anywhere in five of the six creators, so the flag was frozen at mount. The
  // old redirect-to-the-list hid that: you left the screen before you could
  // press Save Draft twice. Now that saving keeps you here, the flag has to
  // follow the document.
  const [createdId, setCreatedId] = useState(null);
  const documentId = id || createdId;
  const isEditMode = Boolean(documentId);
  // Invoice Papers: the delivery's papers and contracts BESIDE the invoice, so
  // an approver checks one against the other without leaving the screen
  // (ClickUp 2026-10-23, "double screen ... where there are approvals").
  // Remembered per browser; Preview PDF stays as it was.
  const invoicePapers = useInvoicePapersOpen();
  // Who RAISED this document. Dropped by this loader until 2026-08-26, which
  // left Screen 1 showing whoever was logged in instead.
  const [documentIssuerName, setDocumentIssuerName] = useState(null);
  const [invoiceStatus, setInvoiceStatus] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);

  useEffect(() => {
    return () => {
      if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
    };
  }, [previewPdfUrl]);
  
  // Determine if invoice is locked (read-only) based on status
  const isReadOnly = invoiceStatus && !['DRAFT', 'PENDING_APPROVAL', 'REJECTED'].includes(invoiceStatus);
  
  // Validation modal state
  const [showDraftValidationModal, setShowDraftValidationModal] = useState(false);
  const [draftValidationErrors, setDraftValidationErrors] = useState([]);
  const [targetScreenForValidation, setTargetScreenForValidation] = useState(null);
  
  // Load existing invoice if editing
  useEffect(() => {
    const loadInvoice = async () => {
      if (!id) return;
      
      try {
        setIsLoading(true);
        setLoadProblem(null);
        const data = await invoicingService.fetchOutgoingInvoice(id);
        setDocumentIssuerName(data.issuer_name || null);
        
        console.log('========== LOADING INVOICE DATA ==========');
        console.log('Full API response:', data);
        console.log('template field:', data.template);
        console.log('template_type field:', data.template_type);
        console.log('template_type_display field:', data.template_type_display);
        console.log('==========================================');
        
        // Populate invoice data
        setInvoiceData({
          invoice_number: data.invoice_number || '',
          hms_entity: data.hms_entity,
          hms_entity_name: data.hms_entity_name,
          invoice_date: data.invoice_date,
          due_date: data.due_date,
          due_date_basis: data.due_date_basis || 'INVOICE_DATE',
          partner: data.partner,
          partner_name: data.partner_name,
          invoice_address: data.invoice_address || {},
          payment_terms: data.payment_terms,
          internal_reference: data.internal_reference || '',
          supply_reference: data.supply_reference || '',
          currency: data.currency,
          number_format: data.number_format || 'US',
          template: data.template,
          template_type: data.template_type,
          bank_account: data.bank_account,
          cost_center: data.cost_center,
          cost_category: data.cost_category,
          reporting_month: data.reporting_month || '',
          notes: data.notes || '',
          notes_visible_on_invoice: data.notes_visible_on_invoice || false,
          supply_reference_visible_on_invoice: data.supply_reference_visible_on_invoice || false,
          // Pledge: the tick, its bank, and whether the deal keeps it.
          ...pledgeOfDocument(data),
          source_delivery: data.source_delivery || null,
          source_delivery_number: data.source_delivery_number || null,
          source_proforma: data.source_proforma || null,
          source_proforma_number: data.source_proforma_number || null,
          net_amount: data.net_amount,
          vat_amount: data.vat_amount,
          gross_amount: data.gross_amount,
          // Financial-approval stamp fields
          entity_stamp_url: data.entity_stamp_url || null,
          approved_by_name: data.approved_by_name || null,
          approved_at: data.approved_at || null
        });

        // Store invoice status
        setInvoiceStatus(data.status);
        
        // Populate line items
        if (data.line_items) {
          setLineItems(data.line_items);
        }
        
        // Populate vessel info
        if (data.vessel_info) {
          setVesselInfo({
            vessel_name: data.vessel_info.vessel_name || '',
            imo_number: data.vessel_info.imo_number || '',
            delivery_date: data.vessel_info.delivery_date || '',
            bl_number: data.vessel_info.bl_number || '',
            bl_date: data.vessel_info.bl_date || '',
            loading_port: data.vessel_info.loading_port || '',
            discharge_port: data.vessel_info.discharge_port || '',
            incoterms: data.vessel_info.incoterms || ''
          });
        }
        
        setLoadedId(id);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading invoice:', error);
        // Keep the answer and say it on the screen, instead of a toast and a
        // bounce to the list that never said whether it was missing or refused.
        setLoadProblem(describeLoadFailure(error));
        setIsLoading(false);
      }
    };
    
    loadInvoice();
  }, [id, loadAttempt]);
  
  // Load delivery data for pre-fill if deliveryId in URL params
  useEffect(() => {
    const loadDeliveryPrefill = async () => {
      const urlParams = new URLSearchParams(location.search);
      const deliveryId = urlParams.get('deliveryId');
      const partnerId = urlParams.get('partnerId');
      const recapIdsParam = urlParams.get('recapIds');
      const recapIds = recapIdsParam ? recapIdsParam.split(',').map(id => parseInt(id)) : null;
      
      // Only prefill for new invoices (not editing existing)
      if (deliveryId && !id && !sourceDelivery) {
        try {
          setIsLoading(true);
          console.log(`Pre-filling outgoing invoice from delivery ${deliveryId} (partner=${partnerId}, recaps=${recapIds})`);
          
          const deliveryData = await invoicingService.fetchDeliveryForInvoice(
            deliveryId,
            'OUTGOING',
            partnerId ? parseInt(partnerId) : null,
            recapIds
          );
          
          console.log('[CreateOutgoingInvoice] Delivery data received:', deliveryData);
          console.log('[CreateOutgoingInvoice] Line items count:', deliveryData.line_items_data?.length);
          console.log('[CreateOutgoingInvoice] Requested recap IDs:', recapIds);
          
          // Set invoice header data
          setInvoiceData(prev => ({
            ...prev,
            ...deliveryData.invoice_data,
            _prefilled_from_delivery: deliveryId,
            _prefilled_delivery_number: deliveryData.delivery_display_number,
            _selected_recap_ids: recapIds  // Track which recaps were selected
          }));
          
          // Set line items from delivery
          if (deliveryData.line_items_data?.length > 0) {
            console.log('[CreateOutgoingInvoice] Setting line items:', deliveryData.line_items_data);
            setLineItems(deliveryData.line_items_data);
          }
          
          // Set vessel info from delivery
          if (deliveryData.vessel_info_data) {
            setVesselInfo(deliveryData.vessel_info_data);
          }
          
          // Check if any required fields are missing
          const missingFields = [];
          if (!deliveryData.invoice_data.hms_entity) missingFields.push('Entity');
          if (!deliveryData.invoice_data.partner) missingFields.push('Customer');
          
          const deliveryNumber = deliveryData.delivery_display_number || `#${deliveryId}`;
          
          if (missingFields.length > 0) {
            showSuccess(
              `Data pre-filled from Delivery ${deliveryNumber}. ` +
              `Please select: ${missingFields.join(', ')}`
            );
          } else {
            showSuccess(`Data pre-filled from Delivery ${deliveryNumber}`);
          }
          console.log('Outgoing invoice pre-fill complete:', deliveryData);
        } catch (error) {
          console.error('Error loading delivery for pre-fill:', error);
          const errorMessage = error.response?.data?.error || 'Failed to load delivery data. Please fill manually.';
          showError(errorMessage);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadDeliveryPrefill();
  }, [location.search, id, sourceDelivery]);


  // Scroll to top on mount and screen change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentScreen]);
  
  // Load user permissions (including is_financial_approver) on mount if not already loaded
  useEffect(() => {
    const loadUserPermissions = async () => {
      if (!user?.is_financial_approver && user?.id) {
        try {
          const dashboardService = await import('../../services/dashboardService');
          const userInfo = await dashboardService.default.getUserInfo();
          // Update user context if needed (using AuthContext updateUser if available)
          console.log('User permissions loaded:', userInfo);
        } catch (error) {
          console.error('Error loading user permissions:', error);
        }
      }
    };
    loadUserPermissions();
  }, [user?.id]);
  
  // Invoice header data (Screen 1)
  const [invoiceData, setInvoiceData] = useState({
    hms_entity: null,
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: null,
    due_date_basis: 'INVOICE_DATE',
    partner: null,
    partner_name: '',
    invoice_address: {},
    payment_terms: null,
    internal_reference: '',
    supply_reference: '',
    currency: 'USD',
    number_format: 'US',
    template: null,
    template_type: null,
    bank_account: null,
    cost_center: null,
    cost_category: null,
    reporting_month: new Date().toISOString().substring(0, 8) + '01',
    notes: '',
    notes_visible_on_invoice: false,
    supply_reference_visible_on_invoice: false,
    ...pledgeOfDocument(null),
    source_delivery: sourceDelivery?.id || null,
    source_proforma: sourceProforma?.id || null
  });

  // Where this screen hands the user back to. **2026-09-09, HMS:** *"could this
  // stay in the delivery pls"* -- an operator who raised this invoice from
  // their delivery was thrown into the Finance invoice table 1.5s after
  // submitting it. Four exits below used to hard-code '/invoicing/outgoing';
  // they now all ask this one helper, which returns the user to whichever
  // screen they actually arrived from.
  const { returnLabel, goBack, goBackAfterSave } = useDocumentReturn({
    listPath: '/invoicing/outgoing',
    listLabel: 'Outgoing Invoices',
    user,
    sourceDeliveryId: invoiceData.source_delivery || sourceDelivery?.id || null,
    sourceDeliveryNumber: invoiceData.source_delivery_number
      || sourceDelivery?.delivery_unique_number || null,
  });

  // Save & Workflow on a bunker delivery's invoice belongs to the Bunker
  // Operations Approvals group (2026-09-17). The server answers who may press
  // it, with the same rule the save enforces.
  const sendToFinance = useSendToFinance({
    document: 'outgoing',
    id: documentId || null,
    // A new invoice raised from a delivery (?deliveryId=) carries it as
    // `_prefilled_from_delivery` until the first save writes source_delivery.
    deliveryId: invoiceData.source_delivery || invoiceData._prefilled_from_delivery
      || sourceDelivery?.id || null,
  });
  
  // Line items (Screen 2)
  const [lineItems, setLineItems] = useState([]);
  
  // Vessel info (Screen 2, auto-populates from delivery or manual input)
  const [vesselInfo, setVesselInfo] = useState(
    sourceDelivery ? {
      vessel_name: sourceDelivery.vessel_name || '',
      imo_number: sourceDelivery.vessel_imo_number || '',
      delivery_date: sourceDelivery.delivery_date || '',
      bl_number: sourceDelivery.bill_of_lading_number || '',
      bl_date: sourceDelivery.bill_of_lading_date || '',
      loading_port: sourceDelivery.loading_port_location || '',
      discharge_port: sourceDelivery.destination_port_location || '',
      incoterms: sourceDelivery.incoterms_code || ''
    } : {
      vessel_name: '',
      imo_number: '',
      delivery_date: '',
      bl_number: '',
      bl_date: '',
      loading_port: '',
      discharge_port: '',
      incoterms: ''
    }
  );

  // --- An open draft shows LIVE figures from the deal behind it ------------
  //
  // Every money figure on an invoice raised from a delivery is a copy: the
  // tonnage comes from the signed bunker delivery note when there is one and
  // the recap's quantity when there is not, and the price always comes from
  // the recap. The copy used to be taken once, when the draft was raised, and
  // never taken again -- so correcting the price on the recap the next morning
  // left the draft showing yesterday's number with nothing on screen to say so.
  //
  // Opening a draft now re-reads those figures from the same address the
  // "create from delivery" flow uses (which runs the mapper, so the order of
  // preference is the mapper's, not this screen's guess), and says on screen
  // where the figures came from and when. Only quantity, unit price and
  // currency move: the description, the VAT rate and the GL account may have
  // been chosen by hand here and the deal has no opinion on them.
  //
  // Drafts only. A document sitting in an approver's queue must not have its
  // numbers move while they are looking at them, and an issued one is frozen
  // outright -- the server refuses changes to the deal behind it from that
  // moment (deliveries/signals.py).
  const [sourceRefresh, setSourceRefresh] = useState(null);
  const sourceRefreshedForRef = useRef(null);

  useEffect(() => {
    const deliveryId = invoiceData.source_delivery;
    if (!id || invoiceStatus !== 'DRAFT' || !deliveryId) return undefined;
    // Wait until the draft's own lines have loaded — there is nothing to
    // compare against before that, and refreshing against an empty list would
    // read as "the deal changed shape".
    if (!lineItems.length) return undefined;
    if (sourceRefreshedForRef.current === id) return undefined;
    sourceRefreshedForRef.current = id;

    let cancelled = false;

    const refreshFromSource = async () => {
      const deliveryNumber = invoiceData.source_delivery_number || `#${deliveryId}`;
      try {
        const sourceData = await invoicingService.fetchDeliveryForInvoice(
          deliveryId,
          'OUTGOING',
          invoiceData.partner || null,
          null
        );
        if (cancelled) return;

        const sourceLines = sourceData?.line_items_data || [];
        if (sourceLines.length !== lineItems.length) {
          // The deal gained or lost a line since this draft was raised.
          // Lining them up by position would put one product's tonnage
          // against another product's description, so we change nothing and
          // say so instead.
          setSourceRefresh({
            deliveryNumber,
            checkedAt: new Date(),
            changedCount: 0,
            structureChanged: true,
          });
          return;
        }

        let changedCount = 0;
        const merged = lineItems.map((item, index) => {
          const source = sourceLines[index] || {};
          const nextQuantity = source.quantity;
          const nextUnitPrice = source.unit_price;
          const nextCurrency = source.currency || item.currency;

          const quantityMoved =
            nextQuantity !== undefined &&
            parseFloat(nextQuantity) !== parseFloat(item.quantity || 0);
          const priceMoved =
            nextUnitPrice !== undefined &&
            parseFloat(nextUnitPrice) !== parseFloat(item.unit_price || 0);
          const currencyMoved = nextCurrency !== item.currency;

          if (!quantityMoved && !priceMoved && !currencyMoved) return item;
          changedCount += 1;

          // Exact decimal product rounded to the cent, as the server does it;
          // VAT and gross build on that same net so the line adds up.
          const netAmount = parseFloat(lineNet(nextQuantity ?? item.quantity, nextUnitPrice ?? item.unit_price));
          // Keep whatever VAT rate is actually on this line by carrying its
          // own effective percentage across, rather than re-deriving one: the
          // line serializer does not send the percentage, and inventing 0%
          // here would quietly wipe VAT off a line that has it.
          const oldNet = parseFloat(item.net_amount) || 0;
          const oldVat = parseFloat(item.vat_amount) || 0;
          const vatAmount = oldNet > 0 ? netAmount * (oldVat / oldNet) : 0;

          return {
            ...item,
            quantity: nextQuantity ?? item.quantity,
            unit_price: nextUnitPrice ?? item.unit_price,
            currency: nextCurrency,
            net_amount: netAmount.toFixed(2),
            vat_amount: vatAmount.toFixed(2),
            gross_amount: (netAmount + vatAmount).toFixed(2),
          };
        });

        if (changedCount > 0) {
          setLineItems(merged);
          const netTotal = merged.reduce((sum, i) => sum + (parseFloat(i.net_amount) || 0), 0);
          const vatTotal = merged.reduce((sum, i) => sum + (parseFloat(i.vat_amount) || 0), 0);
          const grossTotal = merged.reduce((sum, i) => sum + (parseFloat(i.gross_amount) || 0), 0);
          setInvoiceData(prev => ({
            ...prev,
            net_amount: netTotal.toFixed(2),
            vat_amount: vatTotal.toFixed(2),
            gross_amount: grossTotal.toFixed(2),
          }));
        }

        setSourceRefresh({
          deliveryNumber,
          checkedAt: new Date(),
          changedCount,
          structureChanged: false,
        });
      } catch (error) {
        if (cancelled) return;
        // A source we could not read is not a reason to hide the draft, but it
        // IS a reason to stop claiming the figures are live. Say which it is.
        console.error('Could not re-read the delivery behind this draft:', error);
        setSourceRefresh({
          deliveryNumber,
          checkedAt: new Date(),
          changedCount: 0,
          structureChanged: false,
          failed: true,
          failureMessage: extractApiError(error),
        });
      }
    };

    refreshFromSource();
    return () => { cancelled = true; };
  }, [id, invoiceStatus, invoiceData.source_delivery, invoiceData.source_delivery_number,
      invoiceData.partner, lineItems]);
  
  // Row 125: with an invoice in the address, only an invoice that actually
  // loaded may be saved -- otherwise the update would write the blank editor
  // over it. Both save paths ask this before choosing update or create.
  const refuseUnloadedSave = () => {
    if (id && loadedId !== id) {
      showError("This invoice hasn't loaded, so it can't be saved. Reload the page and try again.", 8000);
      return true;
    }
    return false;
  };

  const handleSaveDraft = async (modifiedData = null) => {
    if (refuseUnloadedSave()) return;
    try {
      setIsSaving(true);
      
      // Use modified data from Screen3 if provided, otherwise use state
      const dataToUse = modifiedData || invoiceData;
      
      console.log('========== SAVE DRAFT DEBUG ==========');
      console.log('modifiedData:', modifiedData);
      console.log('invoiceData:', invoiceData);
      console.log('dataToUse:', dataToUse);
      console.log('template_type value:', dataToUse.template_type);
      console.log('======================================');
      
      // DRAFT VALIDATION: Only IMT Entity and Partner required
      // Everything else is optional for work in progress
      
      // Extract IDs to check properly (might be object with id, value, or just the ID itself)
      const entityId = typeof dataToUse.hms_entity === 'object' 
        ? (dataToUse.hms_entity?.id || dataToUse.hms_entity?.value)
        : dataToUse.hms_entity;
      const partnerId = typeof dataToUse.partner === 'object' 
        ? (dataToUse.partner?.id || dataToUse.partner?.value)
        : dataToUse.partner;
      
      console.log('Draft validation - Entity ID:', entityId, 'Partner ID:', partnerId);
      console.log('Raw entity:', dataToUse.hms_entity);
      console.log('Raw partner:', dataToUse.partner);
      
      // Check if required fields are missing
      if (!entityId || !partnerId || !dataToUse.template_type) {
        const errors = [];
        if (!entityId) errors.push('Entity is required');
        if (!partnerId) errors.push('Partner (CRM Profile) is required');
        if (!dataToUse.template_type) errors.push('Template Type is required');
        
        setDraftValidationErrors(errors);
        setTargetScreenForValidation(1);
        setShowDraftValidationModal(true);
        setIsSaving(false);
        return;
      }
      
      console.log('Draft validation passed - Entity and Partner are set');
      
      // Clean the payload to avoid circular references
      // Only send IDs for foreign key relationships, not full objects
      // Handle both {id: X} and {value: X, label: Y} formats
      const cleanPayload = {
        // Basic fields
        invoice_date: dataToUse.invoice_date,
        due_date: dataToUse.due_date,
        due_date_basis: dataToUse.due_date_basis,
        currency: dataToUse.currency,
        number_format: dataToUse.number_format || 'US',
        status: 'DRAFT',
        
        // Foreign keys (only IDs) - handle both 'id' and 'value' properties
        hms_entity: typeof dataToUse.hms_entity === 'object' 
          ? (dataToUse.hms_entity?.id || dataToUse.hms_entity?.value) 
          : dataToUse.hms_entity,
        partner: typeof dataToUse.partner === 'object' 
          ? (dataToUse.partner?.id || dataToUse.partner?.value) 
          : dataToUse.partner,
        payment_terms: typeof dataToUse.payment_terms === 'object' 
          ? (dataToUse.payment_terms?.id || dataToUse.payment_terms?.value) 
          : dataToUse.payment_terms,
        template: typeof dataToUse.template === 'object' 
          ? (dataToUse.template?.id || dataToUse.template?.value) 
          : dataToUse.template,
        template_type: dataToUse.template_type,
        bank_account: typeof dataToUse.bank_account === 'object' 
          ? (dataToUse.bank_account?.id || dataToUse.bank_account?.value) 
          : dataToUse.bank_account,
        cost_center: typeof dataToUse.cost_center === 'object' 
          ? (dataToUse.cost_center?.id || dataToUse.cost_center?.value) 
          : dataToUse.cost_center,
        cost_category: typeof dataToUse.cost_category === 'object'
          ? (dataToUse.cost_category?.id || dataToUse.cost_category?.value)
          : dataToUse.cost_category,
        
        // Text fields
        partner_name: dataToUse.partner_name,
        internal_reference: dataToUse.internal_reference || '',
        supply_reference: dataToUse.supply_reference || '',
        notes: dataToUse.notes || '',
        notes_visible_on_invoice: dataToUse.notes_visible_on_invoice || false,
        supply_reference_visible_on_invoice: dataToUse.supply_reference_visible_on_invoice || false,
        // Pledge (and, on the first save, the recaps it came from).
        ...pledgePayload(dataToUse, { isNew: !documentId }),
        
        // Financial fields
        net_amount: dataToUse.net_amount || 0,
        vat_amount: dataToUse.vat_amount || 0,
        gross_amount: dataToUse.gross_amount || 0,
        
        // Source references
        source_delivery: dataToUse.source_delivery,
        source_proforma: dataToUse.source_proforma,
        
        // Dates - reporting_month must be in YYYY-MM-DD format
        reporting_month: dataToUse.reporting_month 
          ? (dataToUse.reporting_month.length === 7 
              ? dataToUse.reporting_month + '-01'  // Convert YYYY-MM to YYYY-MM-DD
              : dataToUse.reporting_month)
          : null,
        
        // JSON fields
        invoice_address: dataToUse.invoice_address || {},
        
        // Nested data - clean line items (remove any circular refs)
        line_items: lineItems.map(item => ({
          line_number: item.line_number,
          item_description: item.item_description,
          product: typeof item.product === 'object' ? item.product?.id : item.product,
          quantity: item.quantity,
          unit: typeof item.unit === 'object' ? item.unit?.id : item.unit,
          unit_price: item.unit_price,
          net_amount: item.net_amount,
          currency: item.currency || dataToUse.currency,
          vat_rate: typeof item.vat_rate === 'object' ? item.vat_rate?.id : item.vat_rate,
          vat_amount: item.vat_amount,
          gross_amount: item.gross_amount,
          gl_account: typeof item.gl_account === 'object' ? item.gl_account?.id : item.gl_account
        })),
        
        // Vessel info - clean it
        vessel_info: vesselInfo ? {
          vessel_name: vesselInfo.vessel_name || '',
          imo_number: vesselInfo.imo_number || '',
          delivery_date: vesselInfo.delivery_date || null,
          bl_number: vesselInfo.bl_number || '',
          bl_date: vesselInfo.bl_date || null,
          loading_port: vesselInfo.loading_port || '',
          discharge_port: vesselInfo.discharge_port || '',
          incoterms: vesselInfo.incoterms || ''
        } : null
      };
      
      console.log('Sending clean payload:', cleanPayload);
      console.log('TEMPLATE_TYPE IN PAYLOAD:', cleanPayload.template_type);
      
      let response;
      if (documentId) {
        response = await invoicingService.updateOutgoingInvoice(documentId, cleanPayload);
      } else {
        response = await invoicingService.createOutgoingInvoice(cleanPayload);
      }
      
      showSuccess(
        response.invoice_number 
          ? `Invoice ${isEditMode ? 'updated' : 'saved'} successfully! Invoice Number: ${response.invoice_number}`
          : `Invoice draft ${isEditMode ? 'updated' : 'saved'} successfully! Submit for approval to generate invoice number.`,
        8000
      );
      
      // Saving a draft KEEPS YOU ON THE DOCUMENT. It used to eject to the list
      // 1.5s later, which is what accounting reported as "it jumps to the
      // table" -- you are mid-edit and the screen throws you out. Only
      // Save & Workflow navigates away now, because there the document really
      // has left your hands.
      //
      // The new id is adopted into STATE, deliberately NOT into the URL:
      // /create and /edit/:id are separate <Route>s inside App.js's <Switch>,
      // so replacing the URL would unmount and remount this screen and drop the
      // user back on step 1. A smaller jump than the old one, but still a jump.
      if (!documentId && response?.id) {
        setCreatedId(response.id);
      }
      
    } catch (error) {
      console.error('Error saving draft:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      // Extract detailed error message from backend
      let errorMessage = 'Failed to save draft. Please check all fields and try again.';
      
      // extractApiError names the row and the field. The old inline version
      // join()ed line_items -- a list of dicts -- into "[object Object]".
      errorMessage = extractApiError(error, errorMessage);
      
      showError(errorMessage, 10000);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSubmitWorkflow = async (modifiedData = null) => {
    if (refuseUnloadedSave()) return;
    try {
      setIsSaving(true);
      
      // Use modified data from Screen3 if provided, otherwise use state
      const dataToUse = modifiedData || invoiceData;
      
      // Extract IDs to validate properly (might be object with id, value, or just the ID itself)
      const entityId = typeof dataToUse.hms_entity === 'object' 
        ? (dataToUse.hms_entity?.id || dataToUse.hms_entity?.value)
        : dataToUse.hms_entity;
      const partnerId = typeof dataToUse.partner === 'object' 
        ? (dataToUse.partner?.id || dataToUse.partner?.value)
        : dataToUse.partner;
      
      console.log('Workflow validation - Entity ID:', entityId, 'Partner ID:', partnerId);
      
      // Validate ALL required fields and collect errors
      const errors = [];
      let targetScreen = null;
      
      // Screen 1 required fields
      if (!entityId) {
        errors.push('Entity is required');
        targetScreen = 1;
      }
      if (!dataToUse.invoice_date) {
        errors.push('Invoice Date is required');
        targetScreen = targetScreen || 1;
      }
      if (!partnerId) {
        errors.push('Partner (CRM Profile) is required');
        targetScreen = targetScreen || 1;
      }
      if (!dataToUse.payment_terms) {
        errors.push('Payment Terms is required');
        targetScreen = targetScreen || 1;
      }
      if (!dataToUse.template_type) {
        errors.push('Template Type is required');
        targetScreen = targetScreen || 1;
      }
      if (!dataToUse.reporting_month) {
        errors.push('Reporting Month is required');
        targetScreen = targetScreen || 1;
      }
      
      // Screen 2 required fields - line items validation
      if (lineItems.length === 0) {
        errors.push('At least one line item is required');
        targetScreen = targetScreen || 2;
      } else {
        // Validate each line item
        lineItems.forEach((item, index) => {
          if (!item.item_description || !item.item_description.trim()) {
            errors.push(`Line ${index + 1}: Description is required`);
            targetScreen = targetScreen || 2;
          }
          if (!item.quantity || parseFloat(item.quantity) <= 0) {
            errors.push(`Line ${index + 1}: Quantity must be greater than 0`);
            targetScreen = targetScreen || 2;
          }
          if (!item.unit_price || parseFloat(item.unit_price) <= 0) {
            errors.push(`Line ${index + 1}: Unit Price must be greater than 0`);
            targetScreen = targetScreen || 2;
          }
          if (!item.vat_rate) {
            errors.push(`Line ${index + 1}: VAT Rate is required`);
            targetScreen = targetScreen || 2;
          }
        });
      }
      
      // Validate total amount
      const total = parseFloat(dataToUse.gross_amount || 0);
      if (total <= 0) {
        errors.push('Invoice total must be greater than zero');
        targetScreen = targetScreen || 2;
      }
      
      if (errors.length > 0) {
        setDraftValidationErrors(errors);
        setTargetScreenForValidation(targetScreen);
        setShowDraftValidationModal(true);
        setIsSaving(false);
        return;
      }
      
      // Clean the payload to avoid circular references
      // Only send IDs for foreign key relationships, not full objects
      // Handle both {id: X} and {value: X, label: Y} formats
      const cleanPayload = {
        // Basic fields
        invoice_date: dataToUse.invoice_date,
        due_date: dataToUse.due_date,
        due_date_basis: dataToUse.due_date_basis,
        currency: dataToUse.currency,
        number_format: dataToUse.number_format || 'US',
        status: 'PENDING_APPROVAL',
        
        // Foreign keys (only IDs) - handle both 'id' and 'value' properties
        hms_entity: typeof dataToUse.hms_entity === 'object' 
          ? (dataToUse.hms_entity?.id || dataToUse.hms_entity?.value) 
          : dataToUse.hms_entity,
        partner: typeof dataToUse.partner === 'object' 
          ? (dataToUse.partner?.id || dataToUse.partner?.value) 
          : dataToUse.partner,
        payment_terms: typeof dataToUse.payment_terms === 'object' 
          ? (dataToUse.payment_terms?.id || dataToUse.payment_terms?.value) 
          : dataToUse.payment_terms,
        template: typeof dataToUse.template === 'object' 
          ? (dataToUse.template?.id || dataToUse.template?.value) 
          : dataToUse.template,
        template_type: dataToUse.template_type,
        bank_account: typeof dataToUse.bank_account === 'object' 
          ? (dataToUse.bank_account?.id || dataToUse.bank_account?.value) 
          : dataToUse.bank_account,
        cost_center: typeof dataToUse.cost_center === 'object' 
          ? (dataToUse.cost_center?.id || dataToUse.cost_center?.value) 
          : dataToUse.cost_center,
        cost_category: typeof dataToUse.cost_category === 'object'
          ? (dataToUse.cost_category?.id || dataToUse.cost_category?.value)
          : dataToUse.cost_category,
        
        // Text fields
        partner_name: dataToUse.partner_name,
        internal_reference: dataToUse.internal_reference || '',
        supply_reference: dataToUse.supply_reference || '',
        notes: dataToUse.notes || '',
        notes_visible_on_invoice: dataToUse.notes_visible_on_invoice || false,
        supply_reference_visible_on_invoice: dataToUse.supply_reference_visible_on_invoice || false,
        // Pledge (and, on the first save, the recaps it came from).
        ...pledgePayload(dataToUse, { isNew: !documentId }),
        
        // Financial fields
        net_amount: dataToUse.net_amount || 0,
        vat_amount: dataToUse.vat_amount || 0,
        gross_amount: dataToUse.gross_amount || 0,
        
        // Source references
        source_delivery: dataToUse.source_delivery,
        source_proforma: dataToUse.source_proforma,
        
        // Dates - reporting_month must be in YYYY-MM-DD format
        reporting_month: dataToUse.reporting_month 
          ? (dataToUse.reporting_month.length === 7 
              ? dataToUse.reporting_month + '-01'  // Convert YYYY-MM to YYYY-MM-DD
              : dataToUse.reporting_month)
          : null,
        
        // JSON fields
        invoice_address: dataToUse.invoice_address || {},
        
        // Nested data - clean line items (remove any circular refs)
        line_items: lineItems.map(item => ({
          line_number: item.line_number,
          item_description: item.item_description,
          product: typeof item.product === 'object' ? item.product?.id : item.product,
          quantity: item.quantity,
          unit: typeof item.unit === 'object' ? item.unit?.id : item.unit,
          unit_price: item.unit_price,
          net_amount: item.net_amount,
          currency: item.currency || dataToUse.currency,
          vat_rate: typeof item.vat_rate === 'object' ? item.vat_rate?.id : item.vat_rate,
          vat_amount: item.vat_amount,
          gross_amount: item.gross_amount,
          gl_account: typeof item.gl_account === 'object' ? item.gl_account?.id : item.gl_account
        })),
        
        // Vessel info - clean it
        vessel_info: vesselInfo ? {
          vessel_name: vesselInfo.vessel_name || '',
          imo_number: vesselInfo.imo_number || '',
          delivery_date: vesselInfo.delivery_date || null,
          bl_number: vesselInfo.bl_number || '',
          bl_date: vesselInfo.bl_date || null,
          loading_port: vesselInfo.loading_port || '',
          discharge_port: vesselInfo.discharge_port || '',
          incoterms: vesselInfo.incoterms || ''
        } : null
      };
      
      console.log('Sending clean payload for workflow:', cleanPayload);
      console.log('TEMPLATE_TYPE IN WORKFLOW PAYLOAD:', cleanPayload.template_type);
      
      let response;
      if (documentId) {
        console.log('=== UPDATING DRAFT TO WORKFLOW ===');
        console.log('Invoice ID:', id);
        console.log('Payload status:', cleanPayload.status);
        response = await invoicingService.updateOutgoingInvoice(documentId, cleanPayload);
        console.log('Backend response:', response);
        console.log('Response status:', response.status);
        console.log('Response invoice_number:', response.invoice_number);
      } else {
        console.log('=== CREATING NEW INVOICE WITH WORKFLOW ===');
        response = await invoicingService.createOutgoingInvoice(cleanPayload);
        console.log('Backend response:', response);
      }
      
      // Update invoice status state
      setInvoiceStatus(response.status || 'PENDING_APPROVAL');
      setInvoiceData(prev => ({
        ...prev,
        invoice_number: response.invoice_number,
        status: response.status || 'PENDING_APPROVAL'
      }));
      
      console.log('Updated invoiceStatus to:', response.status || 'PENDING_APPROVAL');
      console.log('Is financial approver?', user?.is_financial_approver);
      console.log('Is edit mode?', isEditMode);
      
      // Show appropriate message based on invoice number type
      const isDraftNumber = response.invoice_number && response.invoice_number.startsWith('DRAFT-');
      const numberMessage = isDraftNumber 
        ? `Reference: ${response.invoice_number} (Final invoice number will be assigned upon approval)` 
        : `Invoice Number: ${response.invoice_number}`;
      
      showSuccess(
        `Invoice ${isEditMode ? 'updated and ' : ''}submitted successfully! ${numberMessage}. ` +
        `Status: ${response.status || 'Pending Approval'}, Gross Amount: ${response.currency} ${response.gross_amount}. ` +
        `Financial approvers have been notified.`,
        8000
      );
      
      // Submitting hands the document to finance, so this screen IS finished
      // with -- unlike Save Draft, which keeps you here. Where it hands you
      // back to is the question HMS raised on 2026-09-09: an operator goes to
      // their delivery, a finance user goes to the table they came in from.
      setTimeout(() => {
        goBackAfterSave(response?.id || id);
      }, 1500);
      
    } catch (error) {
      console.error('Error submitting invoice:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      // Extract detailed error message from backend
      let errorMessage = 'Failed to submit invoice. Please check all fields and try again.';
      
      // extractApiError names the row and the field. The old inline version
      // join()ed line_items -- a list of dicts -- into "[object Object]".
      errorMessage = extractApiError(error, errorMessage);
      
      showError(errorMessage, 10000);
    } finally {
      setIsSaving(false);
    }
  };
  
  // Helper function to format currency
  const formatCurrency = (amount, currency = 'USD') => {
    if (amount === null || amount === undefined) return '0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  const handlePreviewPdf = async () => {
    try {
      setIsLoadingPdf(true);
      const blob = await invoicingService.downloadOutgoingInvoicePDF(documentId, { preview: true });
      const url = URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error loading PDF preview:', error);
      showError('Failed to load PDF preview. Please try again.');
    } finally {
      setIsLoadingPdf(false);
    }
  };

  const handleClosePdfPreview = () => {
    setShowPdfPreview(false);
    if (previewPdfUrl) {
      URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
  };

  // Shared pill stepper. `completed` keeps the old visited-before meaning:
  // any screen behind the current one is ticked. Free-jump is unchanged.
  const stepperSections = [
    { id: '1', label: 'Header & Partner', completed: currentScreen > 1 },
    { id: '2', label: 'Products & Vessel', completed: currentScreen > 2 },
    { id: '3', label: 'Bank & Notes', completed: currentScreen > 3 }
  ];
  
  // Handle financial approval action
  const handleFinancialApprovalAction = async ({ action, rejection_reason, notes }) => {
    try {
      let response;
      let redirectAfter = true;

      if (action === 'approve') {
        response = await invoicingService.approveInvoice(id, notes);
        console.log('Approve response:', response);

        // Backend returns: { success, message, invoice, number_changed }
        const invoiceNumber = response?.invoice?.invoice_number || invoiceData.invoice_number || 'Invoice';
        showSuccess(`${invoiceNumber} stamped & approved successfully!`, 8000);
      } else if (action === 'reject') {
        response = await invoicingService.rejectInvoice(id, rejection_reason);
        console.log('Reject response:', response);
        showSuccess(`Invoice rejected. The submitter has been notified.`, 8000);
      } else if (action === 'undo') {
        response = await invoicingService.unapproveInvoice(id);
        console.log('Undo response:', response);
        showSuccess(`Approval withdrawn. The invoice is pending approval again.`, 8000);
        // Stay on the page so the approver can re-stamp if needed
        redirectAfter = false;
      }

      // Reload invoice to get updated status + stamp provenance
      const updatedInvoice = await invoicingService.fetchOutgoingInvoice(id);
      setInvoiceStatus(updatedInvoice.status);
      setInvoiceData(prev => ({
        ...prev,
        invoice_number: updatedInvoice.invoice_number,
        approved_by_name: updatedInvoice.approved_by_name || null,
        approved_at: updatedInvoice.approved_at || null,
        entity_stamp_url: updatedInvoice.entity_stamp_url || prev.entity_stamp_url || null
      }));

      // Redirect to invoice list after brief delay (not for undo)
      if (redirectAfter) {
        setTimeout(() => goBackAfterSave(id), 2000);
      }

    } catch (error) {
      console.error('Error processing approval action:', error);
      console.error('Error details:', error.response?.data);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to process approval action';
      showError(errorMessage, 8000);
    }
  };
  
  if (isLoading) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="invoice-page-container outgoing-invoice-screen">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading invoice...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (id && loadProblem) {
    return (
      <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="invoice-page-container outgoing-invoice-screen">
          <RecordLoadProblem
            problem={loadProblem}
            recordName="invoice"
            areaName="invoicing"
            backLabel={returnLabel}
            onBack={goBack}
            onRetry={() => setLoadAttempt((n) => n + 1)}
          />
        </div>
      </Layout>
    );
  }

  // Mode plus the "came from" note, in the shared header's title slot. The old
  // all-caps was the legacy header's own styling; the shared title carries no
  // transform.
  const headerTitle = [
    isReadOnly ? 'View Outgoing Invoice' : (isEditMode ? 'Edit Outgoing Invoice' : 'Create Outgoing Invoice'),
    sourceDelivery && '(from delivery)',
    sourceProforma && '(from pro-forma)',
  ].filter(Boolean).join(' ');

  return (
    <Layout showLeftNavSidebar={true} showRightSidebar={false}>
        <div className="invoice-page-container outgoing-invoice-screen">
        {/* The arrow goes back to wherever this screen was opened from
            (useDocumentReturn); backLabel is its "Back to ..." wording. */}
        <HMSPageHeader
          onBack={goBack}
          backLabel={returnLabel}
          title={headerTitle}
          kicker="Finance"
          className="invoice-header--papers"
          actions={
            <>
              {/* The delivery link rides in the actions cluster as a chip; it
                  used to sit under the old header's subtitle line. */}
              <DeliveryLinkBadge
                deliveryId={invoiceData.source_delivery || sourceDelivery?.id}
                deliveryNumber={invoiceData.source_delivery_number || sourceDelivery?.delivery_unique_number}
              />

              {id && <PinToDeskButton entityType="outgoing_invoice" objectId={id} />}

              {/* Action buttons - only show if NOT approved */}
              {invoiceStatus !== 'APPROVED' && (
                <>
                  <button
                    className="btn-secondary"
                    onClick={() => handleSaveDraft()}
                    disabled={isSaving}
                    style={{ minWidth: '120px' }}
                  >
                    <FontAwesomeIcon icon={faSave} /> Save Draft
                  </button>
                  {sendToFinance.showSendButton && (
                    <button
                      className="btn-primary"
                      onClick={() => handleSubmitWorkflow()}
                      disabled={isSaving}
                      style={{ minWidth: '160px' }}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} /> Save & Workflow
                    </button>
                  )}
                </>
              )}

              {/* Preview PDF - available on any saved invoice (drafts included) so PMs can sanity-check formatting.
                  IMT: Abacus sync sits next to it, for approved invoices only. */}
              {isEditMode && (
                <>
                  <button
                    className="btn-primary"
                    onClick={handlePreviewPdf}
                    disabled={isLoadingPdf}
                    style={{ minWidth: '160px' }}
                  >
                    <FontAwesomeIcon icon={faFilePdf} /> {isLoadingPdf ? 'Loading...' : 'Preview PDF'}
                  </button>
                  {documentId && (

                    <AbacusStatus model="OutgoingInvoice" recordId={documentId} />

                  )}
                </>
              )}

              {isEditMode && (
                <button
                  className="btn-secondary"
                  onClick={invoicePapers.toggle}
                  style={{ minWidth: '140px' }}
                  title="Show the delivery papers and contracts beside this invoice"
                  aria-pressed={invoicePapers.isOpen}
                  data-testid="invoice-papers-toggle"
                >
                  <FontAwesomeIcon icon={faColumns} /> Invoice Papers
                </button>
              )}
            </>
          }
        />
        
        {/* Progress Indicator */}
        {/* [ Invoice | Invoice Papers ]: the same split as the delivery's
            Contracts & Notes. Closed, the invoice renders exactly as before. */}
        <div className={`record-split invoice-papers-split${documentId && invoicePapers.isOpen ? ' record-split--open' : ''}`}>
        <div className="record-split-content invoice-papers-record">
        <div className="hms-stepper-bar">
          <HMSStepper
            sections={stepperSections}
            activeId={String(currentScreen)}
            onStepClick={(id) => setCurrentScreen(Number(id))}
          />
        </div>

        {/* Why Save & Workflow is absent on a bunker invoice (2026-09-17) --
            with the screen's other banners, in their shape. */}
        {invoiceStatus !== 'APPROVED' && (
          <SendToFinanceNote sendToFinance={sendToFinance} />
        )}

        {/* Where the figures on this draft came from, and when.
            Shown on every draft raised from a delivery -- not only when
            something moved -- because "these numbers are live" is the thing a
            person needs to know before they send the invoice, and silence
            reads the same whether we checked or not. */}
        {sourceRefresh && (
          <div
            className="invoice-source-provenance"
            data-testid="invoice-source-provenance"
            style={{
              margin: '0 0 16px 0',
              padding: '10px 14px',
              borderRadius: '6px',
              borderLeft: `4px solid ${
                sourceRefresh.failed || sourceRefresh.structureChanged ? '#c77700' : '#2e7d32'
              }`,
              background: sourceRefresh.failed || sourceRefresh.structureChanged
                ? '#fff8e6' : '#f1f7f1',
              color: '#333',
              fontSize: '13px',
            }}
          >
            <FontAwesomeIcon
              icon={
                sourceRefresh.failed || sourceRefresh.structureChanged
                  ? faExclamationTriangle
                  : faCheckCircle
              }
              style={{ marginRight: '8px' }}
            />
            {sourceRefresh.failed && (
              <span>
                Could not check Delivery {sourceRefresh.deliveryNumber} just now, so the
                figures below are the ones last saved.{' '}
                {sourceRefresh.failureMessage}
              </span>
            )}
            {!sourceRefresh.failed && sourceRefresh.structureChanged && (
              <span>
                Delivery {sourceRefresh.deliveryNumber} now has a different number of
                product lines than this draft. Nothing was changed here — raise the
                invoice again from the delivery so the lines match.
              </span>
            )}
            {!sourceRefresh.failed && !sourceRefresh.structureChanged && (
              <span>
                {sourceRefresh.changedCount > 0
                  ? `Figures updated from Delivery ${sourceRefresh.deliveryNumber} on `
                  : `Figures checked against Delivery ${sourceRefresh.deliveryNumber} on `}
                {sourceRefresh.checkedAt.toLocaleDateString(undefined, {
                  day: 'numeric', month: 'short', year: 'numeric',
                })}
                {sourceRefresh.changedCount > 0
                  ? ` — ${sourceRefresh.changedCount} line${
                      sourceRefresh.changedCount === 1 ? '' : 's'
                    } changed. Save the draft to keep them.`
                  : ' — nothing has moved.'}
              </span>
            )}
          </div>
        )}
        
        {/* Financial Approver Actions - Show only for financial approvers when invoice is pending approval */}
        {isEditMode && invoiceStatus === 'PENDING_APPROVAL' && user?.is_financial_approver && (
          <div className="financial-approver-banner">
            <div className="financial-approver-banner-content">
              <div className="financial-approver-banner-icon">
                <FontAwesomeIcon icon={faUserShield} />
              </div>
              <div className="financial-approver-banner-text">
                <h4>Financial Approval Required</h4>
                <p>This invoice is pending financial approval. As a Financial Approver, you can approve or reject this invoice.</p>
              </div>
              <button
                className="financial-approver-action-btn"
                onClick={() => setShowApprovalModal(true)}
              >
                <FontAwesomeIcon icon={faCheckCircle} />
                Approve / Reject
              </button>
            </div>
          </div>
        )}

        {/* Approved Banner - financial approvers can undo (un-stamp) before the invoice is sent */}
        {isEditMode && invoiceStatus === 'APPROVED' && user?.is_financial_approver && (
          <div className="financial-approver-banner">
            <div className="financial-approver-banner-content">
              <div className="financial-approver-banner-icon">
                <FontAwesomeIcon icon={faCheckCircle} />
              </div>
              <div className="financial-approver-banner-text">
                <h4>Invoice Approved (Stamped)</h4>
                <p>
                  This invoice has been stamped and approved
                  {invoiceData.approved_by_name ? ` by ${invoiceData.approved_by_name}` : ''}.
                  You can undo the approval while it has not yet been sent.
                </p>
              </div>
              <button
                className="financial-approver-action-btn"
                onClick={() => setShowApprovalModal(true)}
              >
                <FontAwesomeIcon icon={faRotateLeft} />
                Undo Approval
              </button>
            </div>
          </div>
        )}

        {/* Invoice Adjustments Banner - Show when invoice has approved credit/debit notes */}
        {isEditMode && invoiceData.has_adjustments && (
          <div className="invoice-adjustments-banner">
            <div className="invoice-adjustments-banner-content">
              <div className="invoice-adjustments-banner-icon">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <div className="invoice-adjustments-banner-text">
                <h4>This Invoice Has Been Adjusted</h4>
                <div className="adjustment-details">
                  {invoiceData.adjustment_summary?.credit_notes_count > 0 && (
                    <div className="adjustment-line">
                      <span>{invoiceData.adjustment_summary.credit_notes_count} Credit Note(s):</span>
                      <span className="adjustment-amount credit">
                        -{formatCurrency(invoiceData.total_credits_applied, invoiceData.currency)}
                      </span>
                    </div>
                  )}
                  {invoiceData.adjustment_summary?.debit_notes_count > 0 && (
                    <div className="adjustment-line">
                      <span>{invoiceData.adjustment_summary.debit_notes_count} Debit Note(s):</span>
                      <span className="adjustment-amount debit">
                        +{formatCurrency(invoiceData.total_debits_applied, invoiceData.currency)}
                      </span>
                    </div>
                  )}
                  <div className="adjustment-line totals">
                    <span>Original Amount:</span>
                    <span>{formatCurrency(invoiceData.gross_amount, invoiceData.currency)}</span>
                  </div>
                  <div className="adjustment-line totals net">
                    <span><strong>Net Amount Due:</strong></span>
                    <span><strong>{formatCurrency(invoiceData.net_amount_due, invoiceData.currency)}</strong></span>
                  </div>
                </div>
              </div>
              <button
                className="invoice-adjustments-action-btn"
                onClick={() => setShowAdjustmentModal(true)}
              >
                <FontAwesomeIcon icon={faFileInvoice} />
                View Adjustments
              </button>
            </div>
          </div>
        )}
        
        {/* Screen Content */}
        <div className="invoice-screens-container">
          {currentScreen === 1 && (
            <OutgoingInvoiceScreen1
              invoiceData={invoiceData}
              setInvoiceData={setInvoiceData}
              vesselInfo={vesselInfo}
              setVesselInfo={setVesselInfo}
              onNext={() => setCurrentScreen(2)}
              sourceDelivery={sourceDelivery}
              sourceProforma={sourceProforma}
              isReadOnly={isReadOnly}
              issuerName={documentIssuerName}
              isPersisted={Boolean(id)}
            />
          )}
          
          {currentScreen === 2 && (
            <OutgoingInvoiceScreen2
              invoiceData={invoiceData}
              setInvoiceData={setInvoiceData}
              lineItems={lineItems}
              setLineItems={setLineItems}
              vesselInfo={vesselInfo}
              setVesselInfo={setVesselInfo}
              onPrevious={() => setCurrentScreen(1)}
              onNext={() => setCurrentScreen(3)}
              isReadOnly={isReadOnly}
            />
          )}
          
          {currentScreen === 3 && (
            <OutgoingInvoiceScreen3
              invoiceData={invoiceData}
              setInvoiceData={setInvoiceData}
              lineItems={lineItems}
              vesselInfo={vesselInfo}
              onPrevious={() => setCurrentScreen(2)}
              invoiceStatus={invoiceStatus}
              isReadOnly={isReadOnly}
            />
          )}
        </div>
        </div>

        {documentId && (
          <DocumentsSplitPane
            isOpen={invoicePapers.isOpen}
            onClose={invoicePapers.close}
            title="Invoice Papers"
          >
            <RelatedDocumentsPane kind="outgoing" invoiceId={documentId} />
          </DocumentsSplitPane>
        )}
        </div>
        
        {/* Financial Approval Modal */}
        {showApprovalModal && (
          <FinancialApprovalModal
            isOpen={showApprovalModal}
            onClose={() => setShowApprovalModal(false)}
            invoice={{
              ...invoiceData,
              status: invoiceStatus,
              id: id
            }}
            isFinancialApprover={user?.is_financial_approver}
            onApprovalAction={handleFinancialApprovalAction}
            entityStampUrl={invoiceData.entity_stamp_url}
            allowUndo={true}
          />
        )}

        {/* Invoice Adjustment Modal */}
        {showAdjustmentModal && (
          <InvoiceAdjustmentModal
            isOpen={showAdjustmentModal}
            onClose={() => setShowAdjustmentModal(false)}
            adjustmentSummary={invoiceData.adjustment_summary}
            currency={invoiceData.currency}
            onNavigateToCreditNote={(id) => history.push(`/invoicing/credit-notes/${id}`)}
            onNavigateToDebitNote={(id) => history.push(`/invoicing/debit-notes/${id}`)}
          />
        )}
        
        {/* Draft/Workflow Validation Modal */}
        <InvoiceValidationModal
          isOpen={showDraftValidationModal}
          onClose={() => {
            setShowDraftValidationModal(false);
            if (targetScreenForValidation && targetScreenForValidation !== currentScreen) {
              setCurrentScreen(targetScreenForValidation);
            }
          }}
          title="Complete Required Fields"
          errors={draftValidationErrors}
          fieldIds={[]}
          buttonText="Complete Fields"
        />
        
        {/* PDF Preview Modal */}
        <PdfPreviewModal
          isOpen={showPdfPreview}
          pdfUrl={previewPdfUrl}
          onClose={handleClosePdfPreview}
          fileName={`Invoice_${invoiceData.invoice_number || 'draft'}.pdf`}
        />

        {/* Footer */}
        <Footer />
      </div>
    </Layout>
  );
};

export default CreateOutgoingInvoice;

